import React, { useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, Select, MenuItem, FormControl, InputLabel, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, Checkbox, Drawer, Tabs, Tab, Grid, TextField, InputAdornment, Menu, Divider, Paper, TablePagination, Tooltip } from '@mui/material';
import { SearchIcon, FilterIcon, DownloadIcon, MoreVerticalIcon, CheckCircle2Icon, XCircleIcon, ClockIcon, ShieldAlertIcon, ArrowRightLeftIcon, FileTextIcon, EyeIcon, AlertTriangleIcon, CheckIcon, XIcon, ActivityIcon, BarChart3Icon, LandmarkIcon, WalletIcon, BuildingIcon, GlobeIcon, HistoryIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
interface EnhancedTransactionsProps {
  onLogout: () => void;
}
// ─── MOCK DATA GENERATOR ─────────────────────────────────────────────
const REGIONS = [{
  id: 'GH',
  name: 'Ghana',
  flag: '🇬🇭',
  currency: 'GHS'
}, {
  id: 'NG',
  name: 'Nigeria',
  flag: '🇳🇬',
  currency: 'NGN'
}, {
  id: 'KE',
  name: 'Kenya',
  flag: '🇰🇪',
  currency: 'KES'
}, {
  id: 'SN',
  name: 'Senegal',
  flag: '🇸🇳',
  currency: 'XOF'
}, {
  id: 'UG',
  name: 'Uganda',
  flag: '🇺🇬',
  currency: 'UGX'
}];
const CORRIDORS = ['GH→UK', 'NG→US', 'KE→UK', 'SN→FR', 'GH→NG', 'UG→UK'];
const PARTNERS = ['GTBank Ghana', 'Paga Ghana', 'Flutterwave Nigeria', 'M-Pesa Kenya', 'Orange Money Senegal'];
const STATUSES = ['SUCCESS', 'IN_PROGRESS', 'FAILED', 'IN_REVIEW', 'PENDING_INQUIRY'];
const PAYOUT_TYPES = ['FT', 'IBFT', 'OTC'];
const generateMockTransactions = (count: number) => {
  return Array.from({
    length: count
  }).map((_, i) => {
    const region = REGIONS[i % REGIONS.length];
    const status = STATUSES[Math.floor(Math.random() * STATUSES.length)];
    const complianceStatus = status === 'IN_REVIEW' ? 'FLAGGED' : Math.random() > 0.8 ? 'PENDING' : 'CLEARED';
    const settlementStatus = status === 'SUCCESS' ? 'SETTLED' : status === 'FAILED' ? 'FAILED' : 'PENDING';
    return {
      id: `TXN-ECO-${new Date().getFullYear()}-${String(i + 1).padStart(5, '0')}`,
      region: region.name,
      flag: region.flag,
      corridor: CORRIDORS[i % CORRIDORS.length],
      partner: PARTNERS[i % PARTNERS.length],
      remitter: `Remitter ${i + 1}`,
      beneficiary: `Beneficiary ${i + 1}`,
      amountFCY: Math.floor(Math.random() * 5000) + 100,
      currencyFCY: ['USD', 'EUR', 'GBP'][i % 3],
      amountLocal: Math.floor(Math.random() * 50000) + 1000,
      currencyLocal: region.currency,
      payoutType: PAYOUT_TYPES[i % PAYOUT_TYPES.length],
      status: status,
      complianceStatus: complianceStatus,
      settlementStatus: settlementStatus,
      createdAt: new Date(Date.now() - Math.floor(Math.random() * 100000000)).toISOString(),
      lifecycle: [{
        step: 'Received',
        status: 'PASS',
        time: '10:00 AM',
        duration: '0.1s'
      }, {
        step: 'Screening',
        status: complianceStatus === 'FLAGGED' ? 'FAIL' : 'PASS',
        time: '10:01 AM',
        duration: '1.2s'
      }, {
        step: 'Title Fetch',
        status: status === 'FAILED' ? 'FAIL' : 'PASS',
        time: '10:02 AM',
        duration: '0.8s'
      }, {
        step: 'Balance Check',
        status: 'PASS',
        time: '10:02 AM',
        duration: '0.5s'
      }, {
        step: 'Posting',
        status: status === 'IN_PROGRESS' ? 'PENDING' : 'PASS',
        time: '10:03 AM',
        duration: '2.1s'
      }],
      compliance: {
        aml: complianceStatus === 'FLAGGED' ? 'High Risk Match' : 'Clear',
        kyc: 'Verified',
        sanctions: 'No Match',
        history: [{
          time: '10:01 AM',
          action: 'Automated Screening',
          result: complianceStatus
        }]
      },
      settlement: {
        method: PAYOUT_TYPES[i % PAYOUT_TYPES.length],
        account: `VA-${region.id}-${String(i).padStart(4, '0')}`,
        balance: `$${(Math.random() * 100000).toFixed(2)}`,
        fxRate: (Math.random() * 100 + 10).toFixed(2),
        timeline: [{
          time: '10:05 AM',
          event: 'Funds Reserved'
        }, {
          time: '10:10 AM',
          event: settlementStatus === 'SETTLED' ? 'Settlement Confirmed' : 'Pending Settlement'
        }]
      },
      audit: [{
        time: '10:00 AM',
        action: 'Transaction Created',
        user: 'System',
        old: '-',
        new: 'Created'
      }, {
        time: '10:01 AM',
        action: 'Compliance Check',
        user: 'System',
        old: 'Pending',
        new: complianceStatus
      }]
    };
  });
};
const MOCK_DATA = generateMockTransactions(45);
// ─── STYLES ──────────────────────────────────────────────────────────
const thSx = {
  fontWeight: 700,
  fontSize: '0.7rem',
  color: '#0052CC',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  bgcolor: '#F3F5F7',
  borderBottom: '2px solid #E5E7EB',
  py: 1.5,
  px: 2,
  whiteSpace: 'nowrap' as const
};
const tdSx = {
  fontSize: '0.82rem',
  color: '#1F2937',
  py: 1.2,
  px: 2,
  borderBottom: '1px solid #F3F5F7'
};
// ─── HELPERS ─────────────────────────────────────────────────────────
const getStatusColor = (status: string) => {
  switch (status) {
    case 'SUCCESS':
      return {
        bg: '#0B79231A',
        text: '#0B7923'
      };
    case 'FAILED':
      return {
        bg: '#AE2A191A',
        text: '#AE2A19'
      };
    case 'IN_PROGRESS':
      return {
        bg: '#0052CC1A',
        text: '#0052CC'
      };
    case 'IN_REVIEW':
      return {
        bg: '#B386001A',
        text: '#B38600'
      };
    case 'PENDING_INQUIRY':
      return {
        bg: '#6B72801A',
        text: '#6B7280'
      };
    default:
      return {
        bg: '#F3F5F7',
        text: '#1F2937'
      };
  }
};
const getComplianceIcon = (status: string) => {
  switch (status) {
    case 'CLEARED':
      return <CheckCircle2Icon size={16} color="#0B7923" data-id="element-3717" />;
    case 'FLAGGED':
      return <ShieldAlertIcon size={16} color="#AE2A19" data-id="element-3718" />;
    case 'PENDING':
      return <ClockIcon size={16} color="#B38600" data-id="element-3719" />;
    default:
      return null;
  }
};
// ─── MAIN COMPONENT ──────────────────────────────────────────────────
const EnhancedTransactions: React.FC<EnhancedTransactionsProps> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Filters
  const [regionFilter, setRegionFilter] = useState('All');
  const [corridorFilter, setCorridorFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [payoutFilter, setPayoutFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  // Table State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState<string[]>([]);
  // Drawers
  const [drawerTxn, setDrawerTxn] = useState<any | null>(null);
  const [drawerTab, setDrawerTab] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  // Actions Menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTxn, setMenuTxn] = useState<any | null>(null);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  // Filter Logic
  const filteredData = useMemo(() => {
    return MOCK_DATA.filter(txn => {
      if (regionFilter !== 'All' && txn.region !== regionFilter) return false;
      if (corridorFilter !== 'All' && txn.corridor !== corridorFilter) return false;
      if (statusFilter !== 'All' && txn.status !== statusFilter) return false;
      if (payoutFilter !== 'All' && txn.payoutType !== payoutFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return txn.id.toLowerCase().includes(q) || txn.remitter.toLowerCase().includes(q) || txn.beneficiary.toLowerCase().includes(q);
      }
      return true;
    });
  }, [regionFilter, corridorFilter, statusFilter, payoutFilter, searchQuery]);
  // KPIs
  const kpis = useMemo(() => {
    const total = filteredData.length;
    const success = filteredData.filter(t => t.status === 'SUCCESS').length;
    const failed = filteredData.filter(t => t.status === 'FAILED').length;
    const pending = filteredData.filter(t => ['IN_PROGRESS', 'IN_REVIEW'].includes(t.status)).length;
    const volume = filteredData.reduce((acc, t) => acc + t.amountFCY, 0);
    return {
      total,
      volume: `$${volume.toLocaleString()}`,
      successRate: total ? (success / total * 100).toFixed(1) + '%' : '0%',
      failed,
      pending,
      avgTime: '1.8s'
    };
  }, [filteredData]);
  // Selection
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(n => n.id));
      return;
    }
    setSelected([]);
  };
  const handleSelectOne = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    bgcolor: '#F8F9FA'
  }} data-id="element-3720">
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="enhanced-transactions" data-id="element-3721" />

      <Box component="main" sx={{
      flexGrow: 1,
      ml: 0,
      width: {
        sm: 'calc(100% - 240px)'
      },
      mt: 8
    }} data-id="element-3722">
        <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-3723" />

        <Box sx={{
        p: {
          xs: 2,
          sm: 3
        },
        maxWidth: 1600,
        mx: 'auto',
        pb: 10
      }} data-id="element-3724">
          {/* ═══ HEADER ═══ */}
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }} data-id="element-3725">
            <Box data-id="element-3726">
              <Typography variant="h5" sx={{
              fontWeight: 800,
              color: '#1F2937',
              letterSpacing: '-0.02em'
            }} data-id="element-3727">
                Regional Transactions
              </Typography>
              <Typography variant="body2" sx={{
              color: '#6B7280',
              mt: 0.5
            }} data-id="element-3728">
                Manage and monitor Ecobank cross-border remittances
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<BarChart3Icon size={16} data-id="element-3730" />} onClick={() => setReportOpen(true)} sx={{
            bgcolor: '#0052CC',
            '&:hover': {
              bgcolor: '#0043A8'
            },
            textTransform: 'none',
            fontWeight: 600
          }} data-id="element-3729">
              Corridor Report
            </Button>
          </Box>

          {/* ═══ FILTER BAR ═══ */}
          <Card sx={{
          mb: 3,
          borderRadius: '8px',
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }} data-id="element-3731">
            <CardContent sx={{
            p: 2,
            '&:last-child': {
              pb: 2
            }
          }} data-id="element-3732">
              <Grid container spacing={2} alignItems="center" data-id="element-3733">
                <Grid item xs={12} md={3} data-id="element-3734">
                  <TextField fullWidth size="small" placeholder="Search ID, Name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} InputProps={{
                  startAdornment: <InputAdornment position="start" data-id="element-3736">
                          <SearchIcon size={18} color="#6B7280" data-id="element-3737" />
                        </InputAdornment>
                }} data-id="element-3735" />
                </Grid>
                <Grid item xs={6} md={2} data-id="element-3738">
                  <FormControl fullWidth size="small" data-id="element-3739">
                    <InputLabel data-id="element-3740">Region</InputLabel>
                    <Select value={regionFilter} label="Region" onChange={e => setRegionFilter(e.target.value)} data-id="element-3741">
                      <MenuItem value="All" data-id="element-3742">All Regions</MenuItem>
                      {REGIONS.map(r => <MenuItem key={r.id} value={r.name} data-id="element-3743">
                          {r.flag} {r.name}
                        </MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2} data-id="element-3744">
                  <FormControl fullWidth size="small" data-id="element-3745">
                    <InputLabel data-id="element-3746">Corridor</InputLabel>
                    <Select value={corridorFilter} label="Corridor" onChange={e => setCorridorFilter(e.target.value)} data-id="element-3747">
                      <MenuItem value="All" data-id="element-3748">All Corridors</MenuItem>
                      {CORRIDORS.map(c => <MenuItem key={c} value={c} data-id="element-3749">
                          {c}
                        </MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2} data-id="element-3750">
                  <FormControl fullWidth size="small" data-id="element-3751">
                    <InputLabel data-id="element-3752">Status</InputLabel>
                    <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)} data-id="element-3753">
                      <MenuItem value="All" data-id="element-3754">All Statuses</MenuItem>
                      {STATUSES.map(s => <MenuItem key={s} value={s} data-id="element-3755">
                          {s.replace('_', ' ')}
                        </MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2} data-id="element-3756">
                  <FormControl fullWidth size="small" data-id="element-3757">
                    <InputLabel data-id="element-3758">Payout Type</InputLabel>
                    <Select value={payoutFilter} label="Payout Type" onChange={e => setPayoutFilter(e.target.value)} data-id="element-3759">
                      <MenuItem value="All" data-id="element-3760">All Types</MenuItem>
                      {PAYOUT_TYPES.map(p => <MenuItem key={p} value={p} data-id="element-3761">
                          {p}
                        </MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={1} sx={{
                display: 'flex',
                justifyContent: 'flex-end'
              }} data-id="element-3762">
                  <Tooltip title="More Filters" data-id="element-3763">
                    <IconButton sx={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }} data-id="element-3764">
                      <FilterIcon size={18} color="#4B5563" data-id="element-3765" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* ═══ KPI SUMMARY ROW ═══ */}
          <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap'
        }} data-id="element-3766">
            {[{
            label: 'Total Transactions',
            value: kpis.total,
            color: '#0052CC'
          }, {
            label: 'Total Volume',
            value: kpis.volume,
            color: '#1F2937'
          }, {
            label: 'Success Rate',
            value: kpis.successRate,
            color: '#0B7923'
          }, {
            label: 'Failed',
            value: kpis.failed,
            color: '#AE2A19'
          }, {
            label: 'Pending',
            value: kpis.pending,
            color: '#B38600'
          }, {
            label: 'Avg Processing Time',
            value: kpis.avgTime,
            color: '#6B7280'
          }].map((kpi, i) => <Box key={i} sx={{
            flex: '1 1 150px',
            bgcolor: '#FFFFFF',
            p: 2,
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
          }} data-id="element-3767">
                <Typography variant="caption" sx={{
              color: '#6B7280',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.02em'
            }} data-id="element-3768">
                  {kpi.label}
                </Typography>
                <Typography variant="h5" sx={{
              fontWeight: 800,
              color: kpi.color,
              mt: 0.5
            }} data-id="element-3769">
                  {kpi.value}
                </Typography>
              </Box>)}
          </Box>

          {/* ═══ TRANSACTION TABLE ═══ */}
          <Card sx={{
          borderRadius: '8px',
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          overflow: 'hidden'
        }} data-id="element-3770">
            <TableContainer data-id="element-3771">
              <Table size="small" data-id="element-3772">
                <TableHead data-id="element-3773">
                  <TableRow data-id="element-3774">
                    <TableCell padding="checkbox" sx={thSx} data-id="element-3775">
                      <Checkbox color="primary" indeterminate={selected.length > 0 && selected.length < rowsPerPage} checked={filteredData.length > 0 && selected.length === Math.min(rowsPerPage, filteredData.length)} onChange={handleSelectAll} data-id="element-3776" />
                    </TableCell>
                    <TableCell sx={thSx} data-id="element-3777">Transaction ID</TableCell>
                    <TableCell sx={thSx} data-id="element-3778">Region</TableCell>
                    <TableCell sx={thSx} data-id="element-3779">Corridor</TableCell>
                    <TableCell sx={thSx} data-id="element-3780">Remitter → Beneficiary</TableCell>
                    <TableCell sx={thSx} align="right" data-id="element-3781">
                      Amount
                    </TableCell>
                    <TableCell sx={thSx} data-id="element-3782">Type</TableCell>
                    <TableCell sx={thSx} data-id="element-3783">Status</TableCell>
                    <TableCell sx={thSx} align="center" data-id="element-3784">
                      Compliance
                    </TableCell>
                    <TableCell sx={thSx} align="right" data-id="element-3785">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody data-id="element-3786">
                  {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(txn => {
                  const isSelected = selected.indexOf(txn.id) !== -1;
                  const statusColors = getStatusColor(txn.status);
                  return <TableRow key={txn.id} hover selected={isSelected} sx={{
                    '&:hover': {
                      bgcolor: '#FAFBFC'
                    },
                    cursor: 'pointer'
                  }} onClick={() => setDrawerTxn(txn)} data-id="element-3787">
                          <TableCell padding="checkbox" sx={tdSx} onClick={e => e.stopPropagation()} data-id="element-3788">
                            <Checkbox color="primary" checked={isSelected} onChange={() => handleSelectOne(txn.id)} data-id="element-3789" />
                          </TableCell>
                          <TableCell sx={{
                      ...tdSx,
                      fontFamily: 'monospace',
                      color: '#0052CC',
                      fontWeight: 600
                    }} data-id="element-3790">
                            {txn.id}
                          </TableCell>
                          <TableCell sx={tdSx} data-id="element-3791">
                            <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }} data-id="element-3792">
                              <span data-id="element-3793">{txn.flag}</span>
                              <Typography variant="body2" sx={{
                          fontWeight: 500
                        }} data-id="element-3794">
                                {txn.region}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{
                      ...tdSx,
                      fontWeight: 500
                    }} data-id="element-3795">
                            {txn.corridor}
                          </TableCell>
                          <TableCell sx={tdSx} data-id="element-3796">
                            <Typography variant="body2" sx={{
                        fontWeight: 600,
                        color: '#1F2937'
                      }} data-id="element-3797">
                              {txn.remitter}
                            </Typography>
                            <Typography variant="caption" sx={{
                        color: '#6B7280'
                      }} data-id="element-3798">
                              to {txn.beneficiary}
                            </Typography>
                          </TableCell>
                          <TableCell sx={tdSx} align="right" data-id="element-3799">
                            <Typography variant="body2" sx={{
                        fontWeight: 700
                      }} data-id="element-3800">
                              {txn.amountFCY.toLocaleString()} {txn.currencyFCY}
                            </Typography>
                            <Typography variant="caption" sx={{
                        color: '#6B7280'
                      }} data-id="element-3801">
                              {txn.amountLocal.toLocaleString()}{' '}
                              {txn.currencyLocal}
                            </Typography>
                          </TableCell>
                          <TableCell sx={tdSx} data-id="element-3802">
                            <Chip label={txn.payoutType} size="small" sx={{
                        bgcolor: '#F3F5F7',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 22
                      }} data-id="element-3803" />
                          </TableCell>
                          <TableCell sx={tdSx} data-id="element-3804">
                            <Chip label={txn.status.replace('_', ' ')} size="small" sx={{
                        bgcolor: statusColors.bg,
                        color: statusColors.text,
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: 22
                      }} data-id="element-3805" />
                          </TableCell>
                          <TableCell sx={tdSx} align="center" data-id="element-3806">
                            <Tooltip title={`Compliance: ${txn.complianceStatus}`} data-id="element-3807">
                              <Box sx={{
                          display: 'inline-flex'
                        }} data-id="element-3808">
                                {getComplianceIcon(txn.complianceStatus)}
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={tdSx} align="right" onClick={e => e.stopPropagation()} data-id="element-3809">
                            <IconButton size="small" onClick={e => {
                        setAnchorEl(e.currentTarget);
                        setMenuTxn(txn);
                      }} data-id="element-3810">
                              <MoreVerticalIcon size={16} data-id="element-3811" />
                            </IconButton>
                          </TableCell>
                        </TableRow>;
                })}
                  {filteredData.length === 0 && <TableRow data-id="element-3812">
                      <TableCell colSpan={10} align="center" sx={{
                    py: 6,
                    color: '#6B7280'
                  }} data-id="element-3813">
                        No transactions found matching the filters.
                      </TableCell>
                    </TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={filteredData.length} page={page} onPageChange={(_, newPage) => setPage(newPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }} sx={{
            borderTop: '1px solid #F3F5F7'
          }} data-id="element-3814" />
          </Card>
        </Box>
      </Box>

      {/* ═══ BULK ACTIONS BAR ═══ */}
      <AnimatePresence data-id="element-3815">
        {selected.length > 0 && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1200
      }} data-id="element-3816">
            <Paper sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          px: 3,
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          border: '1px solid #E5E7EB',
          bgcolor: '#1F2937',
          color: '#FFFFFF'
        }} data-id="element-3817">
              <Typography variant="body2" sx={{
            fontWeight: 600
          }} data-id="element-3818">
                {selected.length} transaction{selected.length > 1 ? 's' : ''}{' '}
                selected
              </Typography>
              <Divider orientation="vertical" flexItem sx={{
            borderColor: '#374151'
          }} data-id="element-3819" />
              <Button size="small" startIcon={<CheckIcon size={16} data-id="element-3821" />} sx={{
            color: '#10B981',
            textTransform: 'none',
            fontWeight: 600
          }} data-id="element-3820">
                Approve
              </Button>
              <Button size="small" startIcon={<XIcon size={16} data-id="element-3823" />} sx={{
            color: '#EF4444',
            textTransform: 'none',
            fontWeight: 600
          }} data-id="element-3822">
                Reject
              </Button>
              <Button size="small" startIcon={<DownloadIcon size={16} data-id="element-3825" />} sx={{
            color: '#60A5FA',
            textTransform: 'none',
            fontWeight: 600
          }} data-id="element-3824">
                Export
              </Button>
              <IconButton size="small" onClick={() => setSelected([])} sx={{
            color: '#9CA3AF',
            ml: 1
          }} data-id="element-3826">
                <XIcon size={16} data-id="element-3827" />
              </IconButton>
            </Paper>
          </motion.div>}
      </AnimatePresence>

      {/* ═══ LIFECYCLE DRAWER ═══ */}
      <Drawer anchor="right" open={!!drawerTxn} onClose={() => {
      setDrawerTxn(null);
      setDrawerTab(0);
    }} PaperProps={{
      sx: {
        width: {
          xs: '100%',
          sm: 600
        }
      }
    }} data-id="element-3828">
        {drawerTxn && <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }} data-id="element-3829">
            {/* Drawer Header */}
            <Box sx={{
          p: 3,
          borderBottom: '1px solid #E5E7EB',
          bgcolor: '#F8F9FA'
        }} data-id="element-3830">
              <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2
          }} data-id="element-3831">
                <Box data-id="element-3832">
                  <Typography variant="h6" sx={{
                fontWeight: 800,
                color: '#0052CC',
                fontFamily: 'monospace',
                mb: 0.5
              }} data-id="element-3833">
                    {drawerTxn.id}
                  </Typography>
                  <Box sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center'
              }} data-id="element-3834">
                    <Chip label={drawerTxn.status.replace('_', ' ')} size="small" sx={{
                  bgcolor: getStatusColor(drawerTxn.status).bg,
                  color: getStatusColor(drawerTxn.status).text,
                  fontWeight: 700,
                  fontSize: '0.65rem'
                }} data-id="element-3835" />
                    <Chip label={drawerTxn.corridor} size="small" variant="outlined" sx={{
                  fontWeight: 600,
                  fontSize: '0.65rem'
                }} data-id="element-3836" />
                  </Box>
                </Box>
                <IconButton onClick={() => setDrawerTxn(null)} data-id="element-3837">
                  <XIcon size={20} data-id="element-3838" />
                </IconButton>
              </Box>
              <Tabs value={drawerTab} onChange={(_, v) => setDrawerTab(v)} variant="scrollable" sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0,
              fontSize: '0.8rem',
              textTransform: 'none',
              fontWeight: 600
            }
          }} data-id="element-3839">
                <Tab label="Overview" data-id="element-3840" />
                <Tab label="Lifecycle" data-id="element-3841" />
                <Tab label="Compliance" data-id="element-3842" />
                <Tab label="Settlement" data-id="element-3843" />
                <Tab label="Audit Trail" data-id="element-3844" />
              </Tabs>
            </Box>

            {/* Drawer Content */}
            <Box sx={{
          p: 3,
          flexGrow: 1,
          overflowY: 'auto'
        }} data-id="element-3845">
              {/* TAB 0: OVERVIEW */}
              {drawerTab === 0 && <Grid container spacing={3} data-id="element-3846">
                  <Grid item xs={12} data-id="element-3847">
                    <Typography variant="subtitle2" sx={{
                fontWeight: 700,
                color: '#1F2937',
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-3848">
                      <GlobeIcon size={16} color="#0052CC" data-id="element-3849" /> Regional Details
                    </Typography>
                    <Card variant="outlined" sx={{
                borderRadius: '8px'
              }} data-id="element-3850">
                      <CardContent sx={{
                  p: 2,
                  '&:last-child': {
                    pb: 2
                  }
                }} data-id="element-3851">
                        <Grid container spacing={2} data-id="element-3852">
                          <Grid item xs={6} data-id="element-3853">
                            <Typography variant="caption" color="textSecondary" data-id="element-3854">
                              Region
                            </Typography>
                            <Typography variant="body2" fontWeight={600} data-id="element-3855">
                              {drawerTxn.flag} {drawerTxn.region}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} data-id="element-3856">
                            <Typography variant="caption" color="textSecondary" data-id="element-3857">
                              Partner
                            </Typography>
                            <Typography variant="body2" fontWeight={600} data-id="element-3858">
                              {drawerTxn.partner}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} data-id="element-3859">
                            <Typography variant="caption" color="textSecondary" data-id="element-3860">
                              Payout Type
                            </Typography>
                            <Typography variant="body2" fontWeight={600} data-id="element-3861">
                              {drawerTxn.payoutType}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} data-id="element-3862">
                            <Typography variant="caption" color="textSecondary" data-id="element-3863">
                              Created At
                            </Typography>
                            <Typography variant="body2" fontWeight={600} data-id="element-3864">
                              {new Date(drawerTxn.createdAt).toLocaleString()}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} data-id="element-3865">
                    <Typography variant="subtitle2" sx={{
                fontWeight: 700,
                color: '#1F2937',
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-3866">
                      <BuildingIcon size={16} color="#0052CC" data-id="element-3867" /> Parties &
                      Amounts
                    </Typography>
                    <Card variant="outlined" sx={{
                borderRadius: '8px'
              }} data-id="element-3868">
                      <CardContent sx={{
                  p: 2,
                  '&:last-child': {
                    pb: 2
                  }
                }} data-id="element-3869">
                        <Grid container spacing={2} data-id="element-3870">
                          <Grid item xs={6} data-id="element-3871">
                            <Typography variant="caption" color="textSecondary" data-id="element-3872">
                              Remitter
                            </Typography>
                            <Typography variant="body2" fontWeight={600} data-id="element-3873">
                              {drawerTxn.remitter}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} data-id="element-3874">
                            <Typography variant="caption" color="textSecondary" data-id="element-3875">
                              Beneficiary
                            </Typography>
                            <Typography variant="body2" fontWeight={600} data-id="element-3876">
                              {drawerTxn.beneficiary}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} data-id="element-3877">
                            <Typography variant="caption" color="textSecondary" data-id="element-3878">
                              Amount (FCY)
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color="#0052CC" data-id="element-3879">
                              {drawerTxn.amountFCY.toLocaleString()}{' '}
                              {drawerTxn.currencyFCY}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} data-id="element-3880">
                            <Typography variant="caption" color="textSecondary" data-id="element-3881">
                              Amount (Local)
                            </Typography>
                            <Typography variant="body2" fontWeight={700} data-id="element-3882">
                              {drawerTxn.amountLocal.toLocaleString()}{' '}
                              {drawerTxn.currencyLocal}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>}

              {/* TAB 1: LIFECYCLE */}
              {drawerTab === 1 && <Box data-id="element-3883">
                  <Typography variant="subtitle2" sx={{
              fontWeight: 700,
              color: '#1F2937',
              mb: 3
            }} data-id="element-3884">
                    Transaction Lifecycle
                  </Typography>
                  <Box sx={{
              position: 'relative',
              pl: 2
            }} data-id="element-3885">
                    <Box sx={{
                position: 'absolute',
                left: 23,
                top: 10,
                bottom: 10,
                width: 2,
                bgcolor: '#E5E7EB'
              }} data-id="element-3886" />
                    {drawerTxn.lifecycle.map((step: any, idx: number) => <Box key={idx} sx={{
                display: 'flex',
                gap: 2,
                mb: 3,
                position: 'relative'
              }} data-id="element-3887">
                        <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: step.status === 'PASS' ? '#0B7923' : step.status === 'FAIL' ? '#AE2A19' : '#B38600',
                  mt: 0.5,
                  zIndex: 1,
                  border: '2px solid #FFF'
                }} data-id="element-3888" />
                        <Box sx={{
                  flex: 1
                }} data-id="element-3889">
                          <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }} data-id="element-3890">
                            <Typography variant="body2" sx={{
                      fontWeight: 700,
                      color: '#1F2937'
                    }} data-id="element-3891">
                              {step.step}
                            </Typography>
                            <Typography variant="caption" sx={{
                      color: '#6B7280'
                    }} data-id="element-3892">
                              {step.time}
                            </Typography>
                          </Box>
                          <Box sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 0.5
                  }} data-id="element-3893">
                            <Chip label={step.status} size="small" sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      bgcolor: step.status === 'PASS' ? '#0B79231A' : step.status === 'FAIL' ? '#AE2A191A' : '#B386001A',
                      color: step.status === 'PASS' ? '#0B7923' : step.status === 'FAIL' ? '#AE2A19' : '#B38600'
                    }} data-id="element-3894" />
                            <Typography variant="caption" sx={{
                      color: '#9CA3AF'
                    }} data-id="element-3895">
                              Duration: {step.duration}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>)}
                  </Box>
                </Box>}

              {/* TAB 2: COMPLIANCE */}
              {drawerTab === 2 && <Box data-id="element-3896">
                  <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3
            }} data-id="element-3897">
                    <Typography variant="subtitle2" sx={{
                fontWeight: 700,
                color: '#1F2937'
              }} data-id="element-3898">
                      Compliance Checks
                    </Typography>
                    {drawerTxn.complianceStatus === 'FLAGGED' && <Box sx={{
                display: 'flex',
                gap: 1
              }} data-id="element-3899">
                        <Button size="small" variant="contained" color="success" sx={{
                  textTransform: 'none'
                }} data-id="element-3900">
                          Approve
                        </Button>
                        <Button size="small" variant="contained" color="error" sx={{
                  textTransform: 'none'
                }} data-id="element-3901">
                          Reject
                        </Button>
                      </Box>}
                  </Box>
                  <Grid container spacing={2} sx={{
              mb: 3
            }} data-id="element-3902">
                    <Grid item xs={12} sm={4} data-id="element-3903">
                      <Card variant="outlined" sx={{
                  bgcolor: drawerTxn.compliance.aml === 'Clear' ? '#F0FDF4' : '#FEF2F2',
                  borderColor: drawerTxn.compliance.aml === 'Clear' ? '#BBF7D0' : '#FECACA'
                }} data-id="element-3904">
                        <CardContent sx={{
                    p: 2,
                    '&:last-child': {
                      pb: 2
                    }
                  }} data-id="element-3905">
                          <Typography variant="caption" sx={{
                      fontWeight: 600,
                      color: '#6B7280'
                    }} data-id="element-3906">
                            AML Check
                          </Typography>
                          <Typography variant="body2" sx={{
                      fontWeight: 700,
                      mt: 0.5,
                      color: drawerTxn.compliance.aml === 'Clear' ? '#0B7923' : '#AE2A19'
                    }} data-id="element-3907">
                            {drawerTxn.compliance.aml}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4} data-id="element-3908">
                      <Card variant="outlined" sx={{
                  bgcolor: '#F0FDF4',
                  borderColor: '#BBF7D0'
                }} data-id="element-3909">
                        <CardContent sx={{
                    p: 2,
                    '&:last-child': {
                      pb: 2
                    }
                  }} data-id="element-3910">
                          <Typography variant="caption" sx={{
                      fontWeight: 600,
                      color: '#6B7280'
                    }} data-id="element-3911">
                            KYC Status
                          </Typography>
                          <Typography variant="body2" sx={{
                      fontWeight: 700,
                      mt: 0.5,
                      color: '#0B7923'
                    }} data-id="element-3912">
                            {drawerTxn.compliance.kyc}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4} data-id="element-3913">
                      <Card variant="outlined" sx={{
                  bgcolor: '#F0FDF4',
                  borderColor: '#BBF7D0'
                }} data-id="element-3914">
                        <CardContent sx={{
                    p: 2,
                    '&:last-child': {
                      pb: 2
                    }
                  }} data-id="element-3915">
                          <Typography variant="caption" sx={{
                      fontWeight: 600,
                      color: '#6B7280'
                    }} data-id="element-3916">
                            Sanctions
                          </Typography>
                          <Typography variant="body2" sx={{
                      fontWeight: 700,
                      mt: 0.5,
                      color: '#0B7923'
                    }} data-id="element-3917">
                            {drawerTxn.compliance.sanctions}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>}

              {/* TAB 3: SETTLEMENT */}
              {drawerTab === 3 && <Box data-id="element-3918">
                  <Typography variant="subtitle2" sx={{
              fontWeight: 700,
              color: '#1F2937',
              mb: 2
            }} data-id="element-3919">
                    Settlement Details
                  </Typography>
                  <Card variant="outlined" sx={{
              borderRadius: '8px',
              mb: 3
            }} data-id="element-3920">
                    <CardContent sx={{
                p: 2,
                '&:last-child': {
                  pb: 2
                }
              }} data-id="element-3921">
                      <Grid container spacing={2} data-id="element-3922">
                        <Grid item xs={6} data-id="element-3923">
                          <Typography variant="caption" color="textSecondary" data-id="element-3924">
                            Method
                          </Typography>
                          <Typography variant="body2" fontWeight={600} data-id="element-3925">
                            {drawerTxn.settlement.method}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} data-id="element-3926">
                          <Typography variant="caption" color="textSecondary" data-id="element-3927">
                            Virtual Account
                          </Typography>
                          <Typography variant="body2" fontWeight={600} fontFamily="monospace" data-id="element-3928">
                            {drawerTxn.settlement.account}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} data-id="element-3929">
                          <Typography variant="caption" color="textSecondary" data-id="element-3930">
                            Current Balance
                          </Typography>
                          <Typography variant="body2" fontWeight={700} color="#0052CC" data-id="element-3931">
                            {drawerTxn.settlement.balance}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} data-id="element-3932">
                          <Typography variant="caption" color="textSecondary" data-id="element-3933">
                            Applied FX Rate
                          </Typography>
                          <Typography variant="body2" fontWeight={600} data-id="element-3934">
                            {drawerTxn.settlement.fxRate}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                  <Typography variant="subtitle2" sx={{
              fontWeight: 700,
              color: '#1F2937',
              mb: 2
            }} data-id="element-3935">
                    Settlement Timeline
                  </Typography>
                  {drawerTxn.settlement.timeline.map((event: any, idx: number) => <Box key={idx} sx={{
              display: 'flex',
              justifyContent: 'space-between',
              py: 1,
              borderBottom: '1px solid #F3F5F7'
            }} data-id="element-3936">
                        <Typography variant="body2" sx={{
                color: '#1F2937'
              }} data-id="element-3937">
                          {event.event}
                        </Typography>
                        <Typography variant="caption" sx={{
                color: '#6B7280'
              }} data-id="element-3938">
                          {event.time}
                        </Typography>
                      </Box>)}
                </Box>}

              {/* TAB 4: AUDIT TRAIL */}
              {drawerTab === 4 && <Box data-id="element-3939">
                  <Typography variant="subtitle2" sx={{
              fontWeight: 700,
              color: '#1F2937',
              mb: 2
            }} data-id="element-3940">
                    Audit Log
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{
              borderRadius: '8px'
            }} data-id="element-3941">
                    <Table size="small" data-id="element-3942">
                      <TableHead data-id="element-3943">
                        <TableRow data-id="element-3944">
                          <TableCell sx={{
                      ...thSx,
                      py: 1
                    }} data-id="element-3945">
                            Time
                          </TableCell>
                          <TableCell sx={{
                      ...thSx,
                      py: 1
                    }} data-id="element-3946">
                            Action
                          </TableCell>
                          <TableCell sx={{
                      ...thSx,
                      py: 1
                    }} data-id="element-3947">
                            User
                          </TableCell>
                          <TableCell sx={{
                      ...thSx,
                      py: 1
                    }} data-id="element-3948">
                            Change
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody data-id="element-3949">
                        {drawerTxn.audit.map((log: any, idx: number) => <TableRow key={idx} data-id="element-3950">
                            <TableCell sx={{
                      ...tdSx,
                      color: '#6B7280'
                    }} data-id="element-3951">
                              {log.time}
                            </TableCell>
                            <TableCell sx={{
                      ...tdSx,
                      fontWeight: 600
                    }} data-id="element-3952">
                              {log.action}
                            </TableCell>
                            <TableCell sx={tdSx} data-id="element-3953">{log.user}</TableCell>
                            <TableCell sx={tdSx} data-id="element-3954">
                              <Typography variant="caption" sx={{
                        color: '#AE2A19',
                        textDecoration: 'line-through',
                        mr: 1
                      }} data-id="element-3955">
                                {log.old}
                              </Typography>
                              <Typography variant="caption" sx={{
                        color: '#0B7923',
                        fontWeight: 600
                      }} data-id="element-3956">
                                {log.new}
                              </Typography>
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>}
            </Box>
          </Box>}
      </Drawer>

      {/* ═══ CORRIDOR REPORT PANEL ═══ */}
      <Drawer anchor="right" open={reportOpen} onClose={() => setReportOpen(false)} PaperProps={{
      sx: {
        width: {
          xs: '100%',
          sm: 500
        }
      }
    }} data-id="element-3957">
        <Box sx={{
        p: 3,
        borderBottom: '1px solid #E5E7EB',
        bgcolor: '#F8F9FA',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }} data-id="element-3958">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-3959">
            <BarChart3Icon size={20} color="#0052CC" data-id="element-3960" />
            <Typography variant="h6" sx={{
            fontWeight: 800,
            color: '#1F2937'
          }} data-id="element-3961">
              Corridor Report
            </Typography>
          </Box>
          <IconButton onClick={() => setReportOpen(false)} data-id="element-3962">
            <XIcon size={20} data-id="element-3963" />
          </IconButton>
        </Box>
        <Box sx={{
        p: 3
      }} data-id="element-3964">
          <FormControl fullWidth size="small" sx={{
          mb: 3
        }} data-id="element-3965">
            <InputLabel data-id="element-3966">Select Corridor</InputLabel>
            <Select defaultValue="GH→UK" label="Select Corridor" data-id="element-3967">
              {CORRIDORS.map(c => <MenuItem key={c} value={c} data-id="element-3968">
                  {c}
                </MenuItem>)}
            </Select>
          </FormControl>

          <Grid container spacing={2} sx={{
          mb: 4
        }} data-id="element-3969">
            <Grid item xs={6} data-id="element-3970">
              <Card variant="outlined" sx={{
              bgcolor: '#F8F9FA'
            }} data-id="element-3971">
                <CardContent sx={{
                p: 2,
                '&:last-child': {
                  pb: 2
                }
              }} data-id="element-3972">
                  <Typography variant="caption" sx={{
                  color: '#6B7280',
                  fontWeight: 600
                }} data-id="element-3973">
                    Weekly Volume
                  </Typography>
                  <Typography variant="h6" sx={{
                  fontWeight: 800,
                  color: '#0052CC'
                }} data-id="element-3974">
                    $1.2M
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} data-id="element-3975">
              <Card variant="outlined" sx={{
              bgcolor: '#F8F9FA'
            }} data-id="element-3976">
                <CardContent sx={{
                p: 2,
                '&:last-child': {
                  pb: 2
                }
              }} data-id="element-3977">
                  <Typography variant="caption" sx={{
                  color: '#6B7280',
                  fontWeight: 600
                }} data-id="element-3978">
                    Success Rate
                  </Typography>
                  <Typography variant="h6" sx={{
                  fontWeight: 800,
                  color: '#0B7923'
                }} data-id="element-3979">
                    96.5%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{
          fontWeight: 700,
          color: '#1F2937',
          mb: 2
        }} data-id="element-3980">
            7-Day Volume Trend
          </Typography>
          <Box sx={{
          height: 200,
          width: '100%',
          mb: 4
        }} data-id="element-3981">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3982">
              <BarChart data={[{
              day: 'Mon',
              vol: 120
            }, {
              day: 'Tue',
              vol: 150
            }, {
              day: 'Wed',
              vol: 180
            }, {
              day: 'Thu',
              vol: 140
            }, {
              day: 'Fri',
              vol: 210
            }, {
              day: 'Sat',
              vol: 90
            }, {
              day: 'Sun',
              vol: 80
            }]} data-id="element-3983">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-3984" />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6B7280'
              }} data-id="element-3985" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6B7280'
              }} data-id="element-3986" />
                <RechartsTooltip cursor={{
                fill: '#F3F5F7'
              }} contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }} data-id="element-3987" />
                <Bar dataKey="vol" fill="#0052CC" radius={[4, 4, 0, 0]} data-id="element-3988" />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <Typography variant="subtitle2" sx={{
          fontWeight: 700,
          color: '#1F2937',
          mb: 2
        }} data-id="element-3989">
            Reconciliation Status
          </Typography>
          <TableContainer component={Paper} variant="outlined" data-id="element-3990">
            <Table size="small" data-id="element-3991">
              <TableHead data-id="element-3992">
                <TableRow data-id="element-3993">
                  <TableCell sx={thSx} data-id="element-3994">Date</TableCell>
                  <TableCell sx={thSx} data-id="element-3995">Status</TableCell>
                  <TableCell sx={thSx} align="right" data-id="element-3996">
                    Discrepancy
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody data-id="element-3997">
                <TableRow data-id="element-3998">
                  <TableCell sx={tdSx} data-id="element-3999">Today</TableCell>
                  <TableCell sx={tdSx} data-id="element-4000">
                    <Chip label="PENDING" size="small" sx={{
                    height: 20,
                    fontSize: '0.6rem',
                    bgcolor: '#B386001A',
                    color: '#B38600'
                  }} data-id="element-4001" />
                  </TableCell>
                  <TableCell sx={tdSx} align="right" data-id="element-4002">
                    -
                  </TableCell>
                </TableRow>
                <TableRow data-id="element-4003">
                  <TableCell sx={tdSx} data-id="element-4004">Yesterday</TableCell>
                  <TableCell sx={tdSx} data-id="element-4005">
                    <Chip label="MATCHED" size="small" sx={{
                    height: 20,
                    fontSize: '0.6rem',
                    bgcolor: '#0B79231A',
                    color: '#0B7923'
                  }} data-id="element-4006" />
                  </TableCell>
                  <TableCell sx={tdSx} align="right" data-id="element-4007">
                    $0.00
                  </TableCell>
                </TableRow>
                <TableRow data-id="element-4008">
                  <TableCell sx={tdSx} data-id="element-4009">2 Days Ago</TableCell>
                  <TableCell sx={tdSx} data-id="element-4010">
                    <Chip label="MATCHED" size="small" sx={{
                    height: 20,
                    fontSize: '0.6rem',
                    bgcolor: '#0B79231A',
                    color: '#0B7923'
                  }} data-id="element-4011" />
                  </TableCell>
                  <TableCell sx={tdSx} align="right" data-id="element-4012">
                    $0.00
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{
          mt: 4,
          display: 'flex',
          gap: 2
        }} data-id="element-4013">
            <Button fullWidth variant="contained" startIcon={<DownloadIcon size={16} data-id="element-4015" />} sx={{
            bgcolor: '#0052CC',
            textTransform: 'none'
          }} data-id="element-4014">
              Export PDF
            </Button>
            <Button fullWidth variant="outlined" startIcon={<FileTextIcon size={16} data-id="element-4017" />} sx={{
            textTransform: 'none'
          }} data-id="element-4016">
              Export CSV
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Row Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} PaperProps={{
      sx: {
        width: 200,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        border: '1px solid #E5E7EB'
      }
    }} data-id="element-4018">
        <MenuItem onClick={() => {
        setDrawerTxn(menuTxn);
        setDrawerTab(0);
        setAnchorEl(null);
      }} sx={{
        fontSize: '0.85rem',
        gap: 1.5
      }} data-id="element-4019">
          <EyeIcon size={16} color="#6B7280" data-id="element-4020" /> View Details
        </MenuItem>
        <MenuItem onClick={() => {
        setDrawerTxn(menuTxn);
        setDrawerTab(4);
        setAnchorEl(null);
      }} sx={{
        fontSize: '0.85rem',
        gap: 1.5
      }} data-id="element-4021">
          <HistoryIcon size={16} color="#6B7280" data-id="element-4022" /> View Audit Log
        </MenuItem>
        <Divider data-id="element-4023" />
        <MenuItem onClick={() => setAnchorEl(null)} sx={{
        fontSize: '0.85rem',
        gap: 1.5,
        color: '#AE2A19'
      }} data-id="element-4024">
          <ShieldAlertIcon size={16} color="#AE2A19" data-id="element-4025" /> Flag for Compliance
        </MenuItem>
      </Menu>
    </Box>;
};
export default EnhancedTransactions;